// ========================================
// BLOG HELPDESK (Index Page)
// ========================================

if (!customElements.get('custom-blog-helpdesk')) {
  class CustomBlogHelpdesk extends HTMLElement {
    constructor() {
      super();
      this.searchInput = null;
      this.articles = [];
      this.categories = [];
      this.resultsDropdown = null;
      this.subTopicFilters = null;
      this.activeCategory = null;
      this.activeTag = null;
      this.defaultTopic = this.dataset.defaultTopic || '';
    }

    connectedCallback() {
      this.initHelpdesk();
    }

    initHelpdesk() {
      this.searchInput = this.querySelector('#helpdeskSearch');
      this.resultsDropdown = this.querySelector('.ui-autocomplete');
      this.articles = Array.from(this.querySelectorAll('.helpdesk-article'));
      this.categories = Array.from(this.querySelectorAll('.helpdesk-cat'));
      this.subTopicFilters = this.querySelector('#subTopicFilters');

      const searchableData = this.articles.map((article) => ({
        title: article.dataset.title,
        excerpt: article.dataset.excerpt,
        url: article.dataset.url,
        topics: article.dataset.topics.split(','),
        element: article,
      }));

      this.searchInput.addEventListener('input', (e) => {
        this.showSearchResults(e.target.value, searchableData);
      });

      document.addEventListener('mousedown', (e) => {
        const searchWrap = this.querySelector('.relative-search-wrap');
        if (!searchWrap.contains(e.target)) {
          this.closeAndClear();
        }
      });

      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAndClear();
          this.searchInput.blur();
        }
      });

      this.categories.forEach((cat) =>
        cat.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleCategoryClick(cat);
        })
      );

      window.addEventListener('hashchange', () => {
        this.loadCategoryFromHash();
      });

      this.loadCategoryFromHash();
    }

    closeAndClear() {
      this.searchInput.value = '';
      this.resultsDropdown.innerHTML = '';
      this.resultsDropdown.style.display = 'none';
    }

    loadCategoryFromHash() {
      const hash = window.location.hash.slice(1);

      if (hash) {
        const matchingCategory = this.categories.find(cat => cat.dataset.topic === hash);
        if (matchingCategory) {
          this.categories.forEach(cat => cat.classList.remove('active'));
          matchingCategory.classList.add('active');
          this.activeCategory = hash;
          this.activeTag = null;
          this.updateVisibleArticles();
          this.buildSubTopicFilters();
          return;
        }
      }

      if (this.defaultTopic) {
        const defaultCategory = this.categories.find(cat => cat.dataset.topic === this.defaultTopic);
        if (defaultCategory) {
          this.categories.forEach(cat => cat.classList.remove('active'));
          defaultCategory.classList.add('active');
          this.activeCategory = this.defaultTopic;
          this.updateVisibleArticles();
          this.buildSubTopicFilters();
          return;
        }
      }

      const activeCat = this.querySelector('.helpdesk-cat.active');
      if (activeCat) {
        this.activeCategory = activeCat.dataset.topic;
        this.updateVisibleArticles();
        this.buildSubTopicFilters();
      }
    }

    showSearchResults(query, searchableData) {
      this.resultsDropdown.innerHTML = '';

      if (!query || query.length < 2) {
        this.resultsDropdown.style.display = 'none';
        return;
      }

      const lowerQuery = query.toLowerCase();
      const filteredResults = searchableData
        .map((item) => {
          const titleLower = item.title.toLowerCase();
          const excerptLower = item.excerpt.toLowerCase();
          let score = 0;
          if (titleLower === lowerQuery) score = 4;
          else if (titleLower.startsWith(lowerQuery)) score = 3;
          else if (titleLower.includes(lowerQuery)) score = 2;
          else if (excerptLower.includes(lowerQuery)) score = 1;
          return { ...item, score };
        })
        .filter((item) => item.score > 0)
        .sort((a, b) => b.score - a.score);

      if (filteredResults.length === 0) {
        this.resultsDropdown.innerHTML = `<div class="result-item">No results found</div>`;
        this.resultsDropdown.style.display = 'block';
        return;
      }

      filteredResults.slice(0, 50).forEach((item) => {
        const resultItem = document.createElement('div');
        resultItem.classList.add('result-item');
        resultItem.innerHTML = `<strong>${this.highlightMatch(item.title, query)}</strong>`;
        resultItem.addEventListener('click', () => {
          window.location.href = item.url;
        });
        this.resultsDropdown.appendChild(resultItem);
      });

      this.resultsDropdown.style.display = 'block';
    }

    highlightMatch(text, query) {
      const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
      return text.replace(regex, '<mark>$1</mark>');
    }

    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    navigateToArticle(articleElement) {
      const topics = articleElement.dataset.topics.split(',');
      if (topics[0]) {
        this.activeCategory = topics[0];
        this.categories.forEach((cat) => {
          cat.classList.toggle('active', cat.dataset.topic === this.activeCategory);
        });
        this.activeTag = null;
        this.updateVisibleArticles();
        this.buildSubTopicFilters();
      }

      window.scrollTo({
        top: articleElement.offsetTop - 120,
        behavior: 'smooth',
      });

      this.searchInput.value = '';
      this.resultsDropdown.style.display = 'none';
    }

    handleCategoryClick(categoryElement) {
      const topic = categoryElement.dataset.topic;

      this.categories.forEach((cat) =>
        cat.classList.toggle('active', cat === categoryElement)
      );

      this.activeCategory = topic;
      this.activeTag = null;

      this.updateVisibleArticles();
      this.buildSubTopicFilters();

      window.location.hash = topic;

      const articlesSection = this.querySelector('.helpdesk-articles');
      if (articlesSection) {
        window.scrollTo({
          top: articlesSection.offsetTop - 120,
          behavior: 'smooth',
        });
      }
    }

    buildSubTopicFilters() {
      this.subTopicFilters.innerHTML = '';

      if (!this.activeCategory) return;

      const visibleArticles = this.articles.filter((article) => {
        const topics = article.dataset.topics.split(',');
        return topics.includes(this.activeCategory);
      });

      // Build a handle -> label map from data-tags and data-tags-labels
      const tagLabelMap = {};
      visibleArticles.forEach((article) => {
        const handles = article.dataset.tags.split(',').filter(t => t.trim() !== '');
        const labels = (article.dataset.tagsLabels || '').split(',').filter(t => t.trim() !== '');
        handles.forEach((handle, i) => {
          if (!tagLabelMap[handle]) tagLabelMap[handle] = labels[i] || handle;
        });
      });

      const allTags = new Set(Object.keys(tagLabelMap));

      if (allTags.size > 0) {
        const allTag = document.createElement('button');
        allTag.classList.add('subtopic-tag');
        if (!this.activeTag) allTag.classList.add('active');
        allTag.textContent = 'All';
        allTag.addEventListener('click', () => this.handleTagClick(null));
        this.subTopicFilters.appendChild(allTag);

        Array.from(allTags).sort().forEach((tag) => {
          const tagButton = document.createElement('button');
          tagButton.classList.add('subtopic-tag');
          if (this.activeTag === tag) tagButton.classList.add('active');
          tagButton.textContent = tagLabelMap[tag] || tag;
          tagButton.dataset.tag = tag;
          tagButton.addEventListener('click', () => this.handleTagClick(tag));
          this.subTopicFilters.appendChild(tagButton);
        });

        this.subTopicFilters.classList.add('visible');
      } else {
        this.subTopicFilters.classList.remove('visible');
      }
    }

    handleTagClick(tag) {
      this.activeTag = tag;

      const tagButtons = this.subTopicFilters.querySelectorAll('.subtopic-tag');
      tagButtons.forEach((btn) => {
        if (tag === null) {
          btn.classList.toggle('active', btn.textContent === 'All');
        } else {
          btn.classList.toggle('active', btn.dataset.tag === tag);
        }
      });

      this.updateVisibleArticles();
    }

    updateVisibleArticles() {
      const articlesContainer = this.querySelector('.helpdesk-articles');
      let hasVisibleArticles = false;

      this.articles.forEach((article) => {
        const topics = article.dataset.topics.split(',');
        const tags = article.dataset.tags.split(',');

        const matchesTopic = topics.includes(this.activeCategory);
        const matchesTag = !this.activeTag || tags.includes(this.activeTag);

        const shouldShow = matchesTopic && matchesTag;
        article.classList.toggle('visible', shouldShow);
        if (shouldShow) hasVisibleArticles = true;
      });

      articlesContainer.classList.toggle('visible', hasVisibleArticles);
    }
  }

  customElements.define('custom-blog-helpdesk', CustomBlogHelpdesk);
}

// ========================================
// HELPDESK ARTICLE NAV (Individual Article Page)
// ========================================

if (!customElements.get('helpdesk-article-nav')) {
  class HelpdeskArticleNav extends HTMLElement {
    constructor() {
      super();
      this.searchInput = null;
      this.resultsDropdown = null;
      this.blogHandle = null;
      this.articlesData = [];
    }

    connectedCallback() {
      this.initArticleNav();
    }

    initArticleNav() {
      this.blogHandle = this.dataset.blogHandle;
      this.searchInput = this.querySelector('#helpdeskArticleSearch');
      this.resultsDropdown = this.querySelector('.ui-autocomplete');

      if (!this.searchInput || !this.resultsDropdown) return;

      this.fetchAllArticles();

      this.searchInput.addEventListener('input', (e) => {
        this.showSearchResults(e.target.value);
      });

      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAndClear();
        }
      });

      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) {
          this.closeAndClear();
        }
      });
    }

    closeAndClear() {
      this.searchInput.value = '';
      this.resultsDropdown.innerHTML = '';
      this.resultsDropdown.style.display = 'none';
    }

    async fetchAllArticles() {
      try {
        let page = 1;
        let totalPages = 1;
        const allArticles = [];

        while (page <= totalPages) {
          const response = await fetch(`/blogs/${this.blogHandle}?view=json&page=${page}`);
          if (!response.ok) {
            console.warn('Could not fetch blog articles JSON');
            break;
          }
          const data = await response.json();
          allArticles.push(...(data.articles || []));
          totalPages = data.pages || 1;
          page++;
        }

        this.articlesData = allArticles;
      } catch (error) {
        console.error('Error fetching blog articles:', error);
        this.articlesData = [];
      }
    }

    showSearchResults(query) {
      this.resultsDropdown.innerHTML = '';

      if (!query || query.length < 2) {
        this.resultsDropdown.style.display = 'none';
        return;
      }

      if (!this.articlesData || this.articlesData.length === 0) {
        this.resultsDropdown.innerHTML = `<div class="result-item">Search not available</div>`;
        this.resultsDropdown.style.display = 'block';
        return;
      }

      const lowerQuery = query.toLowerCase();
      const filteredResults = this.articlesData
        .map((article) => {
          const titleLower = article.title.toLowerCase();
          const excerptLower = article.excerpt ? article.excerpt.toLowerCase() : '';
          let score = 0;
          if (titleLower === lowerQuery) score = 4;
          else if (titleLower.startsWith(lowerQuery)) score = 3;
          else if (titleLower.includes(lowerQuery)) score = 2;
          else if (excerptLower.includes(lowerQuery)) score = 1;
          return { ...article, score };
        })
        .filter((article) => article.score > 0)
        .sort((a, b) => b.score - a.score);

      if (filteredResults.length === 0) {
        this.resultsDropdown.innerHTML = `<div class="result-item">No results found</div>`;
        this.resultsDropdown.style.display = 'block';
        return;
      }

      filteredResults.slice(0, 50).forEach((article) => {
        const resultItem = document.createElement('div');
        resultItem.classList.add('result-item');
        resultItem.innerHTML = `<strong>${this.highlightMatch(article.title, query)}</strong>`;
        resultItem.addEventListener('click', () => {
          window.location.href = article.url;
        });
        this.resultsDropdown.appendChild(resultItem);
      });

      this.resultsDropdown.style.display = 'block';
    }

    highlightMatch(text, query) {
      const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
      return text.replace(regex, '<mark>$1</mark>');
    }

    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
  }

  customElements.define('helpdesk-article-nav', HelpdeskArticleNav);
}